import React, { useState, useMemo, useCallback, useRef, type ReactNode } from 'react'

import { LinkOutlined, SearchOutlined, LoadingOutlined, QuestionCircleOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { MultipleValue, SearchValue, SelectValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import type { TableService } from '@shared/table/TableIndex'
import { formatDateTime } from '@utils/dateTime'
import { Modal, Table, Button, Input, Spin, Tooltip, Space } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { brandService } from '@services/admin/brandService'; // Assuming shared component can import admin service for common use
import FullDayRangePicker from '@components/shared/FullDayRangePicker'

interface OutOfStockProductsService extends TableService {
  getOrders: (productId: string, query: Record<string, unknown>) => Promise<{ success: boolean; data?: OutOfStockOrderRow[] }>;
}

interface BrandServiceLike {
  fetchList: (params?: Record<string, unknown>) => Promise<{ data: Array<{ id: string; name: string }> }>;
  [key: string]: unknown;
}

interface MerchantOption {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface SalesChannelOption {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface OutOfStockProductRow {
  id: string;
  name: string;
  sku?: string;
  barcode?: string;
  brand?: string;
  merchant_id?: string;
  shortage_quantity?: number;
  order_count?: number;
  in_transit_quantity?: number;
  activated?: boolean;
  [key: string]: unknown;
}

interface OutOfStockOrderRow {
  id: string;
  name: string;
  merchant_name?: string;
  shortage_quantity?: number;
  created_at?: string;
  placed_at?: string;
  [key: string]: unknown;
}

interface SortedInfo {
  columnKey?: string;
  order?: 'ascend' | 'descend' | null;
}

interface SelectedProduct {
  id: string;
  name: string;
}

interface OutOfStockProductsListProps {
  role?: 'admin' | 'operator';
  service: OutOfStockProductsService; // New
  brandService?: BrandServiceLike; // New for lazyLoad
  merchants?: MerchantOption[];
  salesChannels?: SalesChannelOption[];
}

const OutOfStockProductsList: React.FC<OutOfStockProductsListProps> = ({
  role = 'admin',
  service, // New
  brandService: injectedBrandService, // New for lazyLoad
  merchants = [],
  salesChannels = []
}) => {
  const { t } = useTranslation()
  const [lastModified, setLastModified] = useState(0)
  const [modalVisible, setModalVisible] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct | null>(null)
  const [orders, setOrders] = useState<OutOfStockOrderRow[]>([])
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [sortedInfo, setSortedInfo] = useState<SortedInfo>({})

  // 使用 ref 保存當前篩選條件，確保點擊時總是取得最新值
  const filtersRef = useRef<Record<string, unknown>>({})

  // Use injected brandService or fallback
  const currentBrandService: BrandServiceLike | undefined = injectedBrandService || (brandService as unknown as BrandServiceLike);

  const urlPrefix = role === 'operator' ? '/operator' : '/admin'

  // 將 salesChannels 轉換為 select options 格式
  const channelOptions = useMemo(() => {
    return salesChannels.map(channel => ({
      label: channel.name,
      value: channel.id,
    }))
  }, [salesChannels])

  // 當 TableIndex 的 query 變化時更新 ref
  const handleQueryChange = useCallback((query: Record<string, unknown>) => {
    filtersRef.current = query
  }, [])

  // 顯示指定商品的缺貨訂單列表（從 API 獲取，延遲載入）
  const showProductOrders = useCallback(async (productId: string, productName: string) => {
    setSelectedProduct({ id: productId, name: productName })
    setOrders([])
    setSearchText('')
    setSortedInfo({})
    setModalVisible(true)
    setOrdersLoading(true)

    try {
      // 使用 ref 取得最新的篩選條件
      const result = await service.getOrders(productId, filtersRef.current);
      if (result.success && result.data) {
        setOrders(result.data)
      }
    } catch (error) {
      console.error('Failed to fetch out of stock orders:', error)
    } finally {
      setOrdersLoading(false)
    }
  }, [service])

  // 處理 Table 變化（排序）
  const handleTableChange = (pagination: unknown, filters: unknown, sorter: any) => {
    setSortedInfo(sorter)
  }

  // 過濾並排序訂單資料
  const getFilteredAndSortedOrders = () => {
    let filteredOrders = orders

    // 搜尋過濾
    if (searchText) {
      const searchLower = searchText.toLowerCase()
      filteredOrders = filteredOrders.filter(order =>
        order.name.toLowerCase().includes(searchLower) ||
        (order.merchant_name && order.merchant_name.toLowerCase().includes(searchLower))
      )
    }

    // 排序
    if (sortedInfo.columnKey) {
      filteredOrders = [...filteredOrders].sort((a, b) => {
        const aValue = (a as Record<string, any>)[sortedInfo.columnKey as string]
        const bValue = (b as Record<string, any>)[sortedInfo.columnKey as string]

        if (sortedInfo.order === 'ascend') {
          return aValue > bValue ? 1 : -1
        }
        return aValue < bValue ? 1 : -1

      })
    }

    return filteredOrders
  }

  // Modal 中的訂單表格欄位
  const orderColumns = useMemo(() => {
    const cols: any[] = [
      {
        title: t('models.order.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
        render: (name: string, record: OutOfStockOrderRow) => (
          <a href={`${urlPrefix}/orders/${record.id}`} target="_blank" rel="noopener noreferrer">
            {name}
          </a>
        ),
      },
      {
        title: t('shared.out_of_stock_products.modal.shortage_quantity'),
        dataIndex: 'shortage_quantity',
        key: 'shortage_quantity',
        width: 120,
        sorter: true,
        sortOrder: sortedInfo.columnKey === 'shortage_quantity' ? sortedInfo.order : null,
        render: (quantity: number) => (
          <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
            {quantity}
          </span>
        ),
      },
      {
        title: t('common.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        sorter: true,
        sortOrder: sortedInfo.columnKey === 'created_at' ? sortedInfo.order : null,
        render: (text: string) => formatDateTime(text),
      },
      {
        title: t('models.order.placed_at'),
        dataIndex: 'placed_at',
        key: 'placed_at',
        width: 160,
        sorter: true,
        sortOrder: sortedInfo.columnKey === 'placed_at' ? sortedInfo.order : null,
        render: (text: string) => formatDateTime(text),
      },
    ]

    // Operator 角色需要顯示 Merchant 欄位
    if (role === 'operator') {
      cols.splice(1, 0, {
        title: t('models.merchant.name'),
        dataIndex: 'merchant_name',
        key: 'merchant_name',
        width: 150,
        sorter: true,
        sortOrder: sortedInfo.columnKey === 'merchant_name' ? sortedInfo.order : null,
      })
    }
    return cols
  }, [role, t, sortedInfo, urlPrefix])

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        nameKey: `models.product.name`,
        persist: true,
        sortable: true,
        sortFieldName: 'products.name',
        width: '200px',
        wrap: true,
        cell: ({ id, name }: OutOfStockProductRow) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/products/${id}`} style={{ wordBreak: 'break-word' }}>
              {name}
            </Link>
            <a
              href={`${urlPrefix}/products/${id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      }
    ];

    // Add merchant column for operator role
    if (role === 'operator') {
      cols.push({
        nameKey: `models.merchant.name`,
        wrap: true,
        selector: ({ merchant_id }: OutOfStockProductRow) => {
          return merchants.find((merchant) => merchant.id === merchant_id)?.name
        },
      });
    }

    cols.push(
      {
        nameKey: `models.product.sku`,
        sortable: true,
        sortFieldName: 'products.sku',
        wrap: true,
        selector: ({ sku }: OutOfStockProductRow) => sku,
      },
      {
        nameKey: `models.product.barcode`,
        sortable: true,
        sortFieldName: 'products.barcode',
        wrap: true,
        selector: ({ barcode }: OutOfStockProductRow) => barcode,
      },
      {
        nameKey: `models.product.brand`,
        wrap: true,
        selector: ({ brand }: OutOfStockProductRow) => brand,
      },
      {
        name: (
          <Space size={4}>
            {t('shared.out_of_stock_products.list.columns.shortage_quantity')}
            <Tooltip title={t('shared.out_of_stock_products.list.column_tooltips.shortage_quantity')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: true,
        sortFieldName: 'shortage_quantity',
        width: "100px",
        wrap: true,
        cell: ({ shortage_quantity }: OutOfStockProductRow) => (
          <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
            {shortage_quantity}
          </span>
        ),
      },
      {
        name: (
          <Space size={4}>
            {t('shared.out_of_stock_products.list.columns.in_transit_quantity')}
            <Tooltip title={t('shared.out_of_stock_products.list.column_tooltips.in_transit_quantity')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        // 後端用 preload_page 批次計算，僅在當頁 product_ids 內聚合，無法排序
        width: "100px",
        wrap: true,
        cell: ({ in_transit_quantity }: OutOfStockProductRow) => (
          <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
            {in_transit_quantity ?? 0}
          </span>
        ),
      },
      {
        name: (
          <Space size={4}>
            {t('shared.out_of_stock_products.list.columns.order_count')}
            <Tooltip title={t('shared.out_of_stock_products.list.column_tooltips.order_count')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: true,
        sortFieldName: 'order_count',
        width: "100px",
        wrap: true,
        // 改用獨立 API 獲取訂單列表（延遲載入）
        cell: ({ order_count, id, name }: OutOfStockProductRow) => (
          <span
            style={{
              color: '#1890ff',
              fontWeight: 'bold',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            onClick={() => showProductOrders(id, name)}
          >
            {order_count}
          </span>
        ),
      },
      {
        nameKey: `models.product.activated`,
        sortable: false,  // 布林欄位，排序意義不大
        width: "80px",
        cell: ({ activated }: OutOfStockProductRow) => {
          if (activated) {
            return <CheckOutlined style={{ color: 'green' }} />
          }
          return <CloseOutlined style={{ color: '#bfbfbf' }} />
        }
      }
    );

    return cols;
  }, [role, urlPrefix, merchants, showProductOrders])

  const supportRules = useMemo(() => {
    const rules: any[] = [
      {
        label: t(`models.product.sku`),
        component: MultipleValue,
        inputName: 'products.sku',
        defaultPinned: true,
        createable: true,
        tooltip: {
          title: t('shared.out_of_stock_products.list.filter_tooltips.sku.title'),
          content: t('shared.out_of_stock_products.list.filter_tooltips.sku.content'),
        },
      },
      {
        label: t(`models.product.barcode`),
        component: MultipleValue,
        inputName: 'products.barcode',
        createable: true,
        tooltip: {
          title: t('shared.out_of_stock_products.list.filter_tooltips.barcode.title'),
          content: t('shared.out_of_stock_products.list.filter_tooltips.barcode.content'),
        },
      }
    ];

    // Add merchant filter for operator role
    if (role === 'operator') {
      rules.push({
        label: t(`models.merchant.name`),
        component: MultipleValue,
        inputName: 'products.merchant_id',
        createable: false,
        defaultOptions: merchants.map((merchant) => ({
          label: merchant.name,
          value: merchant.id,
        })),
        tooltip: {
          title: t('shared.out_of_stock_products.list.filter_tooltips.merchant.title'),
          content: t('shared.out_of_stock_products.list.filter_tooltips.merchant.content'),
        },
      });
    }

    rules.push(
      {
        label: t(`models.product.other_barcode`),
        component: MultipleValue,
        inputName: 'product_barcodes.barcode',
        createable: true,
        tooltip: {
          title: t('shared.out_of_stock_products.list.filter_tooltips.other_barcode.title'),
          content: t('shared.out_of_stock_products.list.filter_tooltips.other_barcode.content'),
        },
      },
      {
        label: t(`models.product.name`),
        component: MultipleValue,
        inputName: 'products.name',
        createable: true,
        tooltip: {
          title: t('shared.out_of_stock_products.list.filter_tooltips.name.title'),
          content: t('shared.out_of_stock_products.list.filter_tooltips.name.content'),
        },
      },
      {
        label: t(`models.product.brand`),
        component: MultipleValue,
        inputName: 'products.brand_id',
        createable: false,
        lazyLoad: {
          action: currentBrandService ? (params: Record<string, unknown>) => currentBrandService.fetchList(params) : undefined,
          transform: (data: { data: Array<{ id: string; name: string }> }) => data.data.map((brand) => ({
            label: brand.name,
            value: brand.id,
          })),
        },
        tooltip: {
          title: t('shared.out_of_stock_products.list.filter_tooltips.brand.title'),
          content: t('shared.out_of_stock_products.list.filter_tooltips.brand.content'),
        },
      },
      {
        label: t(`models.product.activated`),
        component: SelectValue,
        inputName: 'products.activated',
        defaultOptions: [
          {
            label: t(`shared.out_of_stock_products.list.filters.activated_true`),
            value: 'true',
          },
          {
            label: t(`shared.out_of_stock_products.list.filters.activated_false`),
            value: 'false',
          },
        ],
        tooltip: {
          title: t('shared.out_of_stock_products.list.filter_tooltips.activated.title'),
          content: t('shared.out_of_stock_products.list.filter_tooltips.activated.content'),
        },
      },
      // 訂單編號篩選
      {
        label: t(`shared.out_of_stock_products.list.filters.order_name`),
        component: MultipleValue,
        inputName: 'orders.name',
        createable: true,
        tooltip: {
          title: t('shared.out_of_stock_products.list.filter_tooltips.order_name.title'),
          content: t('shared.out_of_stock_products.list.filter_tooltips.order_name.content'),
        },
      },
      // 訂單時間篩選
      {
        label: t(`shared.out_of_stock_products.list.filters.order_created_at`),
        component: FullDayRangePicker,

        inputName: 'orders.created_at',
        tooltip: {
          title: t('shared.out_of_stock_products.list.filter_tooltips.order_created_at.title'),
          content: t('shared.out_of_stock_products.list.filter_tooltips.order_created_at.content'),
        },
      },
      {
        label: t(`models.order.placed_at`),
        component: FullDayRangePicker,

        inputName: 'orders.placed_at',
        tooltip: {
          title: t('shared.orders.list.filter_tooltips.placed_at.title'),
          content: t('shared.orders.list.filter_tooltips.placed_at.content'),
        },
      },
      // 通路篩選
      {
        label: t(`shared.out_of_stock_products.list.filters.sales_channel`),
        component: MultipleValue,
        inputName: 'orders.sales_channel_id',
        createable: false,
        defaultOptions: channelOptions,
        tooltip: {
          title: t('shared.out_of_stock_products.list.filter_tooltips.sales_channel.title'),
          content: t('shared.out_of_stock_products.list.filter_tooltips.sales_channel.content'),
        },
      },
      // 單品備註篩選
      {
        label: t(`shared.out_of_stock_products.list.filters.order_item_note`),
        component: MultipleValue,
        inputName: 'order_items.note',
        createable: true,
        tooltip: {
          title: t('shared.out_of_stock_products.list.filter_tooltips.order_item_note.title'),
          content: t('shared.out_of_stock_products.list.filter_tooltips.order_item_note.content'),
        },
      },
      // 訂單備註篩選
      {
        label: t(`shared.out_of_stock_products.list.filters.order_note`),
        component: MultipleValue,
        inputName: 'orders.note',
        createable: true,
        tooltip: {
          title: t('shared.out_of_stock_products.list.filter_tooltips.order_note.title'),
          content: t('shared.out_of_stock_products.list.filter_tooltips.order_note.content'),
        },
      }
    );

    return rules;
  }, [role, t, merchants, currentBrandService, channelOptions])

  return (
    <>
      <TableIndex
        columns={columns}
        searchable={{
          match: 'prefix',
          searchFields: [
            t('models.product.sku'),
            t('models.product.name'),
            t('models.product.barcode'),
          ],
        }}
        columnsEditable={true}
        tableId={`${role === 'operator' ? 'Operator' : 'Admin'}OutOfStockProductsTable`}
        defaultQuery={{
          sorting_field: 'shortage_quantity',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        service={service}
        onQueryChange={handleQueryChange}
        filterHelpExample={t('shared.out_of_stock_products.list.filter_help_example') as string}
        filterDocsUrl={`/docs/${role}/out_of_stock_products/list#features`}
      />

      <Modal
        title={
          selectedProduct
            ? t('shared.out_of_stock_products.modal.title', { product: selectedProduct.name })
            : t('shared.out_of_stock_products.modal.title_default')
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            {t('shared.out_of_stock_products.modal.close')}
          </Button>
        ]}
        width={900}
      >
        <Input
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          placeholder={t('shared.out_of_stock_products.modal.search_placeholder') as string}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ marginBottom: 16 }}
        />
        {ordersLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
          </div>
        ) : (
          <Table
            columns={orderColumns}
            dataSource={getFilteredAndSortedOrders()}
            rowKey="id"
            onChange={handleTableChange}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => t('shared.out_of_stock_products.modal.total_orders', { total }) as string,
            }}
            locale={{
              emptyText: t('shared.out_of_stock_products.modal.no_orders')
            }}
          />
        )}
      </Modal>
    </>
  )
}

export default OutOfStockProductsList
